import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createExpense, updateExpense, uploadExpenseReceipt } from '@/services/expenses';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Expense } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Save, Loader2, FileText } from 'lucide-react';

interface ExpenseFormProps {
  expense?: Expense;
  onSuccess: () => void;
  onCancel: () => void;
}

const EXPENSE_CATEGORIES = [
  'Rent & Shop Lease',
  'Electricity & Utilities',
  'Internet & Telecom',
  'Staff Salary & Food',
  'Packaging & Printing',
  'Freight & Courier Charges',
  'Repair Tools & Consumables',
  'Marketing & Advertising',
  'Office Supplies',
  'Miscellaneous & Refreshments',
];

export const ExpenseForm: React.FC<ExpenseFormProps> = ({ expense, onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const isEdit = !!expense;

  const [date, setDate] = useState<string>(
    expense?.expense_date ? expense.expense_date.slice(0, 10) : new Date().toISOString().slice(0, 10)
  );
  const [category, setCategory] = useState<string>(expense?.category ?? EXPENSE_CATEGORIES[0]);
  const [description, setDescription] = useState<string>(expense?.description ?? '');
  const [vendor, setVendor] = useState<string>(expense?.vendor ?? '');
  const [amount, setAmount] = useState<number>(expense?.amount ?? 0);
  const [paymentMethod, setPaymentMethod] = useState<string>(expense?.payment_method ?? 'cash');
  const [notes, setNotes] = useState<string>(expense?.notes ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!description.trim()) throw new Error('Description is required.');
      if (amount <= 0) throw new Error('Amount must be greater than zero.');

      let receiptPath = expense?.receipt_path ?? null;
      if (file) {
        receiptPath = await uploadExpenseReceipt(file);
      }

      const payload = {
        expense_date: date,
        category,
        description: description.trim(),
        vendor: vendor.trim() || null,
        amount,
        payment_method: paymentMethod,
        receipt_path: receiptPath,
        notes: notes.trim() || null,
      };

      return isEdit ? updateExpense(expense.id, payload) : createExpense(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all });
      onSuccess();
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    mutation.mutate();
  };

  const inputClass = "w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md text-xs text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 text-xs">
      {submitError && <ErrorAlert message={submitError} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block font-bold text-brand-black uppercase mb-1">
            Expense Date <span className="text-brand-red">*</span>
          </label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="block font-bold text-brand-black uppercase mb-1">
            Category <span className="text-brand-red">*</span>
          </label>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
            {EXPENSE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block font-bold text-brand-black uppercase mb-1">
          Description <span className="text-brand-red">*</span>
        </label>
        <input
          placeholder="e.g. Shop monthly electricity bill paid"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={inputClass}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block font-bold text-brand-black uppercase mb-1">Vendor / Payee</label>
          <input
            placeholder="e.g. K-Electric / Landlord"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            className={inputClass}
          />
        </div>

        <div>
          <label className="block font-bold text-brand-black uppercase mb-1">
            Amount (PKR) <span className="text-brand-red">*</span>
          </label>
          <input
            type="number"
            min={1}
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value) || 0)}
            className={`${inputClass} font-bold text-brand-green`}
          />
        </div>

        <div>
          <label className="block font-bold text-brand-black uppercase mb-1">Payment Method</label>
          <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className={inputClass}>
            <option value="cash">Cash</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="mobile_wallet">Mobile Wallet</option>
            <option value="card">Card</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block font-bold text-brand-black uppercase mb-1">Receipt Attachment</label>
        <div className="flex items-center space-x-3 bg-brand-bg p-2.5 rounded border border-brand-border">
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="text-xs text-brand-muted file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-brand-green file:text-white hover:file:bg-brand-green-hover"
          />
          {expense?.receipt_path && !file && (
            <span className="text-[10px] text-brand-green font-semibold flex items-center space-x-1">
              <FileText className="w-3.5 h-3.5" />
              <span>Current receipt saved</span>
            </span>
          )}
        </div>
      </div>

      <div>
        <label className="block font-bold text-brand-black uppercase mb-1">Internal Notes</label>
        <textarea
          rows={2}
          placeholder="Additional context or bill reference details..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className={`${inputClass} resize-none`}
        />
      </div>

      <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
        <button type="button" onClick={onCancel} disabled={mutation.isPending} className="px-4 py-2 font-medium text-brand-muted hover:text-brand-black">
          Cancel
        </button>
        <button
          type="submit"
          disabled={mutation.isPending || !description.trim() || amount <= 0}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover disabled:opacity-50 shadow-sm"
        >
          {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          <span>{mutation.isPending ? 'Saving...' : isEdit ? 'Update Expense' : 'Log Expense'}</span>
        </button>
      </div>
    </form>
  );
};
